// Guided onboarding tours — interactive spotlight coach-marks.
// Each step targets a real element by its data-testid. Copy is bilingual (RU/EN),
// kept inline so the whole feature is self-contained and easy to maintain.

export const pickLang = (lang) => (lang && lang.startsWith('ru') ? 'ru' : 'en');
export const tt = (obj, lang) => (obj ? obj[pickLang(lang)] : '');

export const TOURS = [
  {
    id: 'profile',
    icon: 'UserCog',
    accent: '#FF6B35',
    title: { en: 'Set up your profile', ru: 'Настройте профиль' },
    desc: {
      en: 'Your name, link and details that guests will see. ~1 min.',
      ru: 'Имя, ссылка и данные, которые увидят гости. ~1 мин.',
    },
    nextTour: 'calendar',
    steps: [
      {
        selector: 'nav-settings',
        path: '/settings',
        placement: 'right',
        title: { en: 'This is where you start', ru: 'Начинаем отсюда' },
        body: {
          en: 'Open «My Profile» — everything about your account lives here. We have opened it for you.',
          ru: 'Откройте «Мой профиль» — здесь всё о вашем аккаунте. Мы уже открыли её для вас.',
        },
      },
      {
        selector: 'settings-name-input',
        path: '/settings',
        placement: 'bottom',
        title: { en: 'Your name', ru: 'Ваше имя' },
        body: {
          en: 'Guests see this name on every booking page. Make it the one they will recognise.',
          ru: 'Это имя гости видят на каждой странице записи. Укажите узнаваемое имя.',
        },
      },
      {
        selector: 'settings-slug-input',
        path: '/settings',
        placement: 'bottom',
        title: { en: 'Your personal link', ru: 'Ваша личная ссылка' },
        body: {
          en: 'This becomes your shareable address, e.g. tagdada.com/your-name. Keep it short.',
          ru: 'Это ваш короткий адрес для ссылок, например tagdada.com/your-name.',
        },
      },
      {
        selector: 'settings-company-input',
        path: '/settings',
        placement: 'bottom',
        title: { en: 'Company & website', ru: 'Компания и сайт' },
        body: {
          en: 'Optional — shown to invitees so they instantly trust the invite.',
          ru: 'Необязательно — показывается гостям, чтобы они сразу доверяли приглашению.',
        },
      },
      {
        selector: 'settings-tz-select',
        path: '/settings',
        placement: 'bottom',
        title: { en: 'Your timezone', ru: 'Ваш часовой пояс' },
        body: {
          en: 'We schedule everything in this zone. In-person meetings still show their location time.',
          ru: 'Всё планируется в этом поясе. Очные встречи показываются по времени места проведения.',
        },
      },
      {
        selector: 'settings-save-profile',
        path: '/settings',
        placement: 'top',
        title: { en: 'Save your profile', ru: 'Сохраните профиль' },
        body: {
          en: 'Click Save and you are ready. Next: connect your calendar so we never double-book you.',
          ru: 'Нажмите «Сохранить» — готово. Дальше: подключим календарь, чтобы не было накладок.',
        },
      },
    ],
  },

  {
    id: 'calendar',
    icon: 'CalendarPlus',
    accent: '#22C55E',
    title: { en: 'Connect Yandex Calendar', ru: 'Подключите Яндекс Календарь' },
    desc: {
      en: 'Two-way sync so your busy times are always respected. ~2 min.',
      ru: 'Двусторонняя синхронизация — занятое время всегда учитывается. ~2 мин.',
    },
    nextTour: 'firstevent',
    steps: [
      {
        selector: 'nav-integrations',
        path: '/integrations',
        placement: 'right',
        title: { en: 'Open Connections', ru: 'Откройте «Подключения»' },
        body: {
          en: 'All your calendars and channels live here. We have opened the Connections page.',
          ru: 'Здесь все ваши календари и каналы. Мы открыли страницу «Подключения».',
        },
      },
      {
        selector: 'suggest-tag-yandex',
        path: '/integrations',
        placement: 'bottom',
        title: { en: 'Connect your calendar', ru: 'Подключите календарь' },
        body: {
          en: 'Click «Yandex Calendar» to connect it — a quick step-by-step guide will pop up and walk you through creating the app-password. That is all you need for now!',
          ru: 'Нажмите «Яндекс Календарь», чтобы подключить его — появится короткая пошаговая инструкция, которая поможет создать пароль приложения. Это всё, что нужно сейчас!',
        },
      },
    ],
  },

  {
    id: 'firstevent',
    icon: 'CalendarClock',
    accent: '#9D4CDD',
    title: { en: 'Create your first event', ru: 'Создайте первое событие' },
    desc: {
      en: 'A reusable availability page guests book in one click. ~2 min.',
      ru: 'Страница доступности, которую гости бронируют в один клик. ~2 мин.',
    },
    perk: { en: '+50 free credits for your first event 🎁', ru: '+50 бесплатных кредитов за первое событие 🎁' },
    steps: [
      {
        selector: 'nav-event-types',
        path: '/event-types',
        placement: 'right',
        title: { en: 'Open «Set Availabilities»', ru: 'Откройте «Доступность»' },
        body: {
          en: 'Your reusable booking pages live here. Let us build one together.',
          ru: 'Здесь ваши многоразовые страницы записи. Давайте создадим одну вместе.',
        },
      },
      {
        selector: 'et-name-input',
        path: '/event-types/new/availability',
        placement: 'bottom',
        title: { en: 'Name the meeting', ru: 'Название встречи' },
        body: {
          en: 'e.g. "30-min intro call". Guests see this title before booking.',
          ru: 'например «Вводный звонок 30 мин». Гости видят это название.',
        },
      },
      {
        selector: 'et-description-input',
        path: '/event-types/new/availability',
        placement: 'bottom',
        title: { en: 'Describe it', ru: 'Описание' },
        body: {
          en: 'Tell guests what to expect — this becomes the meeting agenda in their calendar.',
          ru: 'Расскажите гостям, чего ожидать — это станет повесткой в их календаре.',
        },
      },
      {
        selector: 'et-availability-card',
        path: '/event-types/new/availability',
        placement: 'top',
        title: { en: 'Booking rules', ru: 'Правила записи' },
        body: {
          en: 'Buffers, confirmation and flexible duration — fine-tune how guests can book you.',
          ru: 'Буферы, подтверждение и гибкая длительность — настройте, как гости вас бронируют.',
        },
      },
      {
        selector: 'et-save-button',
        path: '/event-types/new/availability',
        placement: 'top',
        title: { en: 'Publish it', ru: 'Опубликуйте' },
        body: {
          en: 'Save and your booking link goes live. Next: let us share it.',
          ru: 'Сохраните — ссылка для записи станет активной. Дальше: поделимся ею.',
        },
      },
      {
        selector: 'first-event-share-actions',
        path: '/event-types',
        placement: 'top',
        title: { en: 'Copy & share your link', ru: 'Скопируйте и поделитесь ссылкой' },
        body: {
          en: 'Tap «Copy link», then paste it on your website, LinkedIn, a Telegram chat or in an email — guests book you in one click. You are all set! 🎉',
          ru: 'Нажмите «Копировать ссылку» и вставьте её на сайт, в LinkedIn, в чат Telegram или в письмо — гости запишутся в один клик. Всё готово! 🎉',
        },
      },
    ],
  },

  {
    id: 'quickmeeting',
    icon: 'Send',
    accent: '#FF6B35',
    group: 'create',
    title: { en: 'Send a quick meeting', ru: 'Отправьте быструю встречу' },
    desc: {
      en: 'Propose a few times and let one person pick. ~1 min.',
      ru: 'Предложите пару вариантов времени — гость выберет. ~1 мин.',
    },
    steps: [
      {
        selector: 'nav-invitations',
        path: '/quick-invites',
        placement: 'right',
        title: { en: 'Open «Your Meetings»', ru: 'Откройте «Ваши встречи»' },
        body: {
          en: 'Best for a one-off meeting with someone specific. We opened the page for you.',
          ru: 'Подходит для разовой встречи с конкретным человеком. Мы открыли страницу.',
        },
      },
      {
        selector: 'qi-page-new',
        path: '/quick-invites',
        placement: 'bottom',
        title: { en: 'Open the composer', ru: 'Откройте форму' },
        body: {
          en: 'Click «Quick meeting». Inside you will: ① name it, ② propose one or several time options, ③ pick people from your contacts (or add them manually), then ④ Send — everyone gets a personal link to pick their time. 🎉',
          ru: 'Нажмите «Быстрая встреча». Внутри вы: ① дадите название, ② предложите один или несколько вариантов времени, ③ выберете людей из контактов (или добавите вручную), затем ④ Отправите — каждый получит личную ссылку для выбора времени. 🎉',
        },
      },
    ],
  },

  {
    id: 'poll',
    icon: 'ListChecks',
    accent: '#F59E0B',
    group: 'create',
    title: { en: 'Run a group poll', ru: 'Запустите опрос' },
    desc: {
      en: 'Let several people vote on the best time. ~2 min.',
      ru: 'Несколько человек голосуют за лучшее время. ~2 мин.',
    },
    steps: [
      {
        selector: 'nav-polls',
        path: '/polls',
        placement: 'right',
        title: { en: 'Open Polls', ru: 'Откройте «Опросы»' },
        body: {
          en: 'Doodle-style voting to find the time that works for the whole group.',
          ru: 'Голосование в стиле Doodle, чтобы найти время для всей группы.',
        },
      },
      {
        selector: 'poll-title-input',
        path: '/polls/new',
        placement: 'bottom',
        title: { en: 'Name your poll', ru: 'Название опроса' },
        body: {
          en: 'e.g. "Team sync — pick a slot". This is the title voters will see.',
          ru: 'например «Созвон команды — выберите слот». Это название увидят голосующие.',
        },
      },
      {
        selector: 'poll-description-input',
        path: '/polls/new',
        placement: 'bottom',
        title: { en: 'Add details', ru: 'Добавьте описание' },
        body: {
          en: 'Tell voters what the meeting is about so they can choose confidently.',
          ru: 'Расскажите, о чём встреча, чтобы голосующие выбирали уверенно.',
        },
      },
      {
        selector: 'poll-wizard-stepper',
        path: '/polls/new',
        placement: 'bottom',
        title: { en: 'Four simple steps', ru: 'Четыре простых шага' },
        body: {
          en: 'Details → time options → people → review. You can move with these steps anytime.',
          ru: 'Детали → варианты времени → люди → проверка. Переключайтесь по шагам в любой момент.',
        },
      },
      {
        selector: 'poll-wizard-next',
        path: '/polls/new',
        placement: 'top',
        title: { en: 'Continue', ru: 'Продолжайте' },
        body: {
          en: 'Next, add 2-3 time options, invite voters, then review & create. We will collect the votes for you.',
          ru: 'Дальше добавьте 2-3 варианта времени, пригласите участников, проверьте и создайте. Голоса соберём за вас.',
        },
      },
    ],
  },

  {
    id: 'queue',
    icon: 'Zap',
    accent: '#9D4CDD',
    group: 'create',
    title: { en: 'Open a live queue', ru: 'Откройте живую очередь' },
    desc: {
      en: 'First-come-first-served walk-ins on a live day. ~2 min.',
      ru: 'Приём по очереди в реальном времени. ~2 мин.',
    },
    steps: [
      {
        selector: 'nav-queues',
        path: '/queues',
        placement: 'right',
        title: { en: 'Open Queues', ru: 'Откройте «Очереди»' },
        body: {
          en: 'Perfect for walk-in days — guests join a live queue and you serve them in order.',
          ru: 'Идеально для дней приёма — гости встают в живую очередь, вы принимаете по порядку.',
        },
      },
      {
        selector: 'queue-title',
        path: '/queue/new',
        placement: 'bottom',
        title: { en: 'Name the queue', ru: 'Название очереди' },
        body: {
          en: 'e.g. "Open office hours". Guests see this when they join.',
          ru: 'например «Часы приёма». Гости видят это при входе в очередь.',
        },
      },
      {
        selector: 'queue-date',
        path: '/queue/new',
        placement: 'bottom',
        title: { en: 'Pick the day', ru: 'Выберите день' },
        body: {
          en: 'Choose the day your queue runs. People can only join during this day.',
          ru: 'Выберите день работы очереди. Войти можно только в этот день.',
        },
      },
      {
        selector: 'queue-start',
        path: '/queue/new',
        placement: 'bottom',
        title: { en: 'Set the time window', ru: 'Задайте интервал' },
        body: {
          en: 'Start and end time. We compute each guest\u2019s estimated turn automatically.',
          ru: 'Начало и конец. Мы автоматически рассчитаем примерное время каждого гостя.',
        },
      },
      {
        selector: 'queue-create',
        path: '/queue/new',
        placement: 'top',
        title: { en: 'Open the queue', ru: 'Откройте очередь' },
        body: {
          en: 'Create it and share the link or QR — guests join live. Done! 🎉',
          ru: 'Создайте и поделитесь ссылкой или QR — гости встают в очередь. Готово! 🎉',
        },
      },
    ],
  },
];

export const getTour = (id) => TOURS.find((t) => t.id === id) || null;
